import React, { useEffect, useMemo, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { Button, Input, Select, Modal, Badge } from "./ui";
import { toast } from "sonner";
import { BASE_CODES } from "./CurrencyMasterDashboard";
import { computePosition, PERIODS, fmt, fmtu, n } from "../lib/equityMath";
import { TrendingUp, TrendingDown, Pencil, Trash2, CalendarClock } from "lucide-react";
import { AppraisalButton } from "./Appraisal";
import { ScheduledActivity } from "./ScheduledActivity";

const today = () => new Date().toISOString().slice(0, 10);
const blankPos = () => ({ name: "", exchange: "", ticker: "", currency: "", current_price: "", settle_account: "", show_sold: true, show_bought: true, show_held: true, show_lending: false, show_income: false, show_tax: false });

// 6 highlightable words -> tab groups. Row order: SPL top, INT bottom.
const WORDS = [
  { key: "sale", label: "Sale", flag: "show_sold" },
  { key: "purchase", label: "Purchase", flag: "show_bought" },
  { key: "lending", label: "Lending", flag: "show_lending" },
  { key: "income", label: "Income", flag: "show_income" },
  { key: "netted", label: "Netted", flag: "show_held" },
  { key: "tax", label: "Tax", flag: "show_tax" },
];
const ROW1 = ["sale", "purchase", "lending"];
const ROW2 = ["income", "netted", "tax"];
const ALL_KEYS = WORDS.map((w) => w.key);
const flagOf = (key) => WORDS.find((w) => w.key === key).flag;

const TONE = { red: "border-red-500/30 bg-red-500/5", green: "border-green-500/30 bg-green-500/5", primary: "border-[var(--primary)]/30 bg-[var(--primary)]/5", amber: "border-amber-500/30 bg-amber-500/5" };
const TONE_TX = { red: "text-red-700 dark:text-red-400", green: "text-green-700 dark:text-green-400", primary: "text-[var(--primary)]", amber: "text-amber-700 dark:text-amber-500" };
const GROUP_DEF = {
  sale: { label: "Sale", tone: "red", cells: (c, cur) => [["Avg sale", `${cur} ${fmt(c.avgSalePrice)}`, "avgsale"], ["Last sale", `${cur} ${fmt(c.lastSalePrice)}`, "lastsale"], ["Last units sold", fmtu(c.lastUnitsSold), "lastsaleunits"]] },
  purchase: { label: "Purchase", tone: "green", cells: (c, cur) => [["Avg purchase", `${cur} ${fmt(c.avgPurchasePrice)}`, "avgbuy"], ["Last purchase", `${cur} ${fmt(c.lastPurchasePrice)}`, "lastbuy"], ["Last units bought", fmtu(c.lastUnitsPurchased), "lastbuyunits"]] },
  lending: { label: "Lending", tone: "amber", form: "lending" },
  income: { label: "Income", tone: "amber", form: "income" },
  netted: { label: "Netted", tone: "primary", cells: (c, cur) => [["Avg carrying", `${cur} ${fmt(c.avgCarryingPrice)}`, "avgcarry"], ["Current carrying", `${cur} ${fmt(c.currentCarryingPrice)}`, "curcarry"], ["Units carried", fmtu(c.unitsCarried), "unitscarried"]] },
  tax: { label: "Tax and Commissions", tone: "amber", form: "tax" },
};

const Cell = ({ label, value, testid, tone }) => (
  <div className="rounded border border-black/10 bg-white/50 px-2 py-1 dark:border-white/10 dark:bg-black/20" data-testid={testid}>
    <div className="text-[9px] uppercase tracking-wide opacity-55">{label}</div>
    <div className={`text-xs font-semibold tabular-nums ${tone || ""}`}>{value}</div>
  </div>
);

// Labeled transaction mini-list (Sell / Buy / Margin / Project) shown in the far-right field.
const TxList = ({ label, tone, items, testid, onDel }) => (
  <div className="rounded border border-black/10 bg-white/50 px-2 py-1 dark:border-white/10 dark:bg-black/20" data-testid={testid}>
    <div className={`text-[9px] font-semibold uppercase tracking-wide ${tone || "opacity-55"}`}>{label}</div>
    {(!items || items.length === 0) ? (
      <div className="text-[10px] opacity-40">—</div>
    ) : (
      <div className="mt-0.5 space-y-0.5">
        {items.slice(0, 4).map((it, i) => (
          <div key={it.id || i} className="flex items-center justify-between gap-1.5 text-[10px] tabular-nums" data-testid={`${testid}-row-${i}`}>
            <span className="font-medium">{it.qty}</span>
            <span className="flex items-center gap-1">
              <span className="opacity-55">{it.date}</span>
              {onDel && it.id && <button onClick={() => onDel(it.id)} data-testid={`${testid}-del-${i}`} className="opacity-40 hover:text-red-500 hover:opacity-100">×</button>}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const II = "w-full rounded border border-black/15 bg-white/70 px-1 py-0.5 text-[10px] tabular-nums dark:border-white/15 dark:bg-black/30";
const LL = "text-[9px] uppercase tracking-wide opacity-55";
const Chk = ({ label, checked, onChange, tid }) => (
  <label className="flex items-center gap-1 text-[10px]"><input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={tid} /> {label}</label>
);
const Num = ({ label, value, onChange, onBlur, tid }) => (
  <label className="block"><span className={LL}>{label}</span><input className={II} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} data-testid={tid} /></label>
);
const Sel = ({ label, value, onChange, options, placeholder, tid }) => (
  <label className="block"><span className={LL}>{label}</span>
    <select className={II} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>
      <option value="">{placeholder || "—"}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);
const ModeSel = ({ value, onChange, tid }) => (
  <div className="flex flex-col gap-0.5">
    <span className={LL}>{TL("Mode")}</span>
    {["manual", "automated", "rule"].map((m) => (
      <label key={m} className="flex items-center gap-1 text-[10px] capitalize"><input type="radio" checked={value === m} onChange={() => onChange(m)} data-testid={`${tid}-${m}`} /> {m}</label>
    ))}
  </div>
);
const PoolSel = ({ label, value, onChange, pools, tid }) => (
  <label className="block"><span className={LL}>{label}</span>
    <select className={II} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>
      <option value="">{TL("— account —")}</option>
      {pools.map((p) => <option key={p.id} value={p.id}>{p.name || p.id}{p.balance != null ? ` (${p.balance})` : ""}</option>)}
    </select>
  </label>
);
const RunBtn = ({ label, onClick, tid }) => (
  <button type="button" onClick={onClick} data-testid={tid} className="rounded bg-[var(--primary)] px-2 py-0.5 text-[10px] font-semibold text-white hover:opacity-90">{label}</button>
);
const DUR_A = [["overnight", "Overnight"], ["72h", "72 hours"], ["weekend", "Weekend"], ["one_week", "1 week"], ["one_month", "1 month"]];
const DUR_B = [["one_month", "1 month"], ["60d", "60 days"], ["90d", "90 days"], ["180d", "180 days"], ["360d", "360 days"]];

function IncomeForm({ pid, pos, c, cur, val, set, commit, pools, adjust }) {
  const dy = c.currentCarryingPrice > 0 ? (n(val.coupon_rate) / c.currentCarryingPrice) * 100 : 0;
  const divAmt = n(val.coupon_rate) * (c.numOnHand || 0);
  return (
    <div className="grid grid-cols-3 gap-2" data-testid={`eq-inc-${pid}`}>
      <div className="space-y-1">
        <div className="text-[10px] font-semibold">{TL("Dividend")}</div>
        <Chk label={TL("Cash")} checked={val.cash} onChange={(v) => set("cash", v)} tid={`eq-inc-cash-${pid}`} />
        <Chk label={TL("DRIP")} checked={val.drip} onChange={(v) => set("drip", v)} tid={`eq-inc-drip-${pid}`} />
        <div className="pt-1"><ModeSel value={val.mode} onChange={(v) => set("mode", v)} tid={`eq-inc-mode-${pid}`} /></div>
      </div>
      <div className="space-y-1">
        <Num label={`Coupon rate (${cur})`} value={val.coupon_rate} onChange={(v) => set("coupon_rate", v, false)} onBlur={commit} tid={`eq-inc-coupon-${pid}`} />
        <Chk label={TL("Weekly")} checked={val.freq_weekly} onChange={(v) => set("freq_weekly", v)} tid={`eq-inc-weekly-${pid}`} />
        <Chk label={TL("Quarterly")} checked={val.freq_quarterly} onChange={(v) => set("freq_quarterly", v)} tid={`eq-inc-quarterly-${pid}`} />
        <Chk label={TL("Annually")} checked={val.freq_annually} onChange={(v) => set("freq_annually", v)} tid={`eq-inc-annually-${pid}`} />
      </div>
      <div className="space-y-1">
        <div><span className={LL}>{TL("Dividend yield")}</span><div className="text-[10px] font-semibold tabular-nums" data-testid={`eq-inc-yield-${pid}`}>{fmt(dy)}%</div></div>
        <PoolSel label={TL("Pay to account")} value={val.pay_to_account} onChange={(v) => set("pay_to_account", v)} pools={pools} tid={`eq-inc-payto-${pid}`} />
        <Num label={`YTD total (${cur})`} value={val.ytd_total} onChange={(v) => set("ytd_total", v, false)} onBlur={commit} tid={`eq-inc-ytd-${pid}`} />
        <RunBtn label={`Pay dividend (${fmt(divAmt)})`} onClick={() => adjust(val.pay_to_account, "credit", divAmt, "equity_dividend", { ticker: pos.ticker, coupon: n(val.coupon_rate), units: c.numOnHand })} tid={`eq-inc-paynow-${pid}`} />
      </div>
    </div>
  );
}

export function TaxForm({ pid, val, set, commit, pools, codes, adjust }) {
  const totalComm = n(val.buy_comm) + n(val.margin_comm) + n(val.sell_comm) + n(val.project_comm);
  return (
    <div className="grid grid-cols-2 gap-3" data-testid={`eq-tax-${pid}`}>
      <div className="space-y-1">
        <Num label={TL("Buy tax")} value={val.buy_comm} onChange={(v) => set("buy_comm", v, false)} onBlur={commit} tid={`eq-tax-buy-${pid}`} />
        <Num label={TL("Margin commissions")} value={val.margin_comm} onChange={(v) => set("margin_comm", v, false)} onBlur={commit} tid={`eq-tax-margin-${pid}`} />
        <div className="h-2" />
        <Num label={TL("Sell tax")} value={val.sell_comm} onChange={(v) => set("sell_comm", v, false)} onBlur={commit} tid={`eq-tax-sell-${pid}`} />
        <Num label={TL("Project commissions")} value={val.project_comm} onChange={(v) => set("project_comm", v, false)} onBlur={commit} tid={`eq-tax-project-${pid}`} />
      </div>
      <div className="flex justify-between gap-2">
        <div className="space-y-1">
          <span className={LL}>{TL("Pay From")}</span>
          <Sel label={TL("Currency")} value={val.pay_from_currency} onChange={(v) => set("pay_from_currency", v)} options={codes} placeholder={TL("— cur —")} tid={`eq-tax-cur-${pid}`} />
          <PoolSel label={TL("Account")} value={val.pay_from_account} onChange={(v) => set("pay_from_account", v)} pools={pools} tid={`eq-tax-acct-${pid}`} />
          <RunBtn label={`Pay fees (${fmt(totalComm)})`} onClick={() => adjust(val.pay_from_account, "debit", totalComm, "equity_fees", { buy: n(val.buy_comm), margin: n(val.margin_comm), sell: n(val.sell_comm), project: n(val.project_comm) })} tid={`eq-tax-paynow-${pid}`} />
        </div>
        <div className="self-end"><ModeSel value={val.mode} onChange={(v) => set("mode", v)} tid={`eq-tax-mode-${pid}`} /></div>
      </div>
    </div>
  );
}

function LendingForm({ pid, pos, cur, val, set, commit, pools, codes, adjust }) {
  const total = n(val.units_lent) * n(val.value_per_unit);
  const Rad = (field, key, label) => (
    <label key={key} className="flex items-center gap-1 text-[10px]"><input type="radio" name={`${field}-${pid}`} checked={val[field] === key} onChange={() => set(field, key)} data-testid={`eq-lend-${field}-${key}-${pid}`} /> {label}</label>
  );
  return (
    <div className="space-y-1.5" data-testid={`eq-lend-${pid}`}>
      <div className="grid grid-cols-4 gap-2">
        <div />
        <Num label={TL("Units lent")} value={val.units_lent} onChange={(v) => set("units_lent", v, false)} onBlur={commit} tid={`eq-lend-units-${pid}`} />
        <Num label={`Value/unit (${cur})`} value={val.value_per_unit} onChange={(v) => set("value_per_unit", v, false)} onBlur={commit} tid={`eq-lend-vpu-${pid}`} />
        <div><span className={LL}>{TL("Total value lent")}</span><div className="text-[10px] font-semibold tabular-nums" data-testid={`eq-lend-total-${pid}`}>{cur} {fmt(total)}</div></div>
      </div>
      <div className="grid grid-cols-4 items-end gap-2">
        <Chk label={TL("In-Margin")} checked={val.margin_sel} onChange={(v) => set("margin_sel", v)} tid={`eq-lend-margin-sel-${pid}`} />
        <Num label={TL("Outlay")} value={val.margin_outlay} onChange={(v) => set("margin_outlay", v, false)} onBlur={commit} tid={`eq-lend-outlay-${pid}`} />
        <Sel label={TL("Currency")} value={val.margin_currency} onChange={(v) => set("margin_currency", v)} options={codes} placeholder={TL("— cur —")} tid={`eq-lend-margin-cur-${pid}`} />
        <PoolSel label={TL("Pay from")} value={val.margin_pay_from} onChange={(v) => set("margin_pay_from", v)} pools={pools} tid={`eq-lend-payfrom-${pid}`} />
      </div>
      <div className="grid grid-cols-4 items-end gap-2">
        <Chk label={TL("Out-Project")} checked={val.project_sel} onChange={(v) => set("project_sel", v)} tid={`eq-lend-project-sel-${pid}`} />
        <Num label={TL("Income")} value={val.project_income} onChange={(v) => set("project_income", v, false)} onBlur={commit} tid={`eq-lend-income-${pid}`} />
        <Sel label={TL("Currency")} value={val.project_currency} onChange={(v) => set("project_currency", v)} options={codes} placeholder={TL("— cur —")} tid={`eq-lend-project-cur-${pid}`} />
        <PoolSel label={TL("Pay to")} value={val.project_pay_to} onChange={(v) => set("project_pay_to", v)} pools={pools} tid={`eq-lend-payto-${pid}`} />
      </div>
      <div className="grid grid-cols-4 gap-2">
        <div className="space-y-0.5">{DUR_A.map(([k, l]) => Rad("duration_a", k, l))}</div>
        <div className="space-y-0.5">{DUR_B.map(([k, l]) => Rad("duration_b", k, l))}</div>
        <div className="col-span-2 flex items-end justify-between gap-2">
          <div className="flex flex-col gap-1">
            <RunBtn label={`Run outlay (${fmt(n(val.margin_outlay))})`} onClick={() => adjust(val.margin_pay_from, "debit", n(val.margin_outlay), "lending_outlay", { ticker: pos.ticker })} tid={`eq-lend-run-outlay-${pid}`} />
            <RunBtn label={`Run income (${fmt(n(val.project_income))})`} onClick={() => adjust(val.project_pay_to, "credit", n(val.project_income), "lending_income", { ticker: pos.ticker })} tid={`eq-lend-run-income-${pid}`} />
          </div>
          <ModeSel value={val.mode} onChange={(v) => set("mode", v)} tid={`eq-lend-mode-${pid}`} />
        </div>
      </div>
    </div>
  );
}

function GroupForm({ kind, pos, c, cur, val, set, commit, pools, codes, adjust }) {
  if (kind === "income") return <IncomeForm pid={pos.id} pos={pos} c={c} cur={cur} val={val} set={set} commit={commit} pools={pools} adjust={adjust} />;
  if (kind === "tax") return <TaxForm pid={pos.id} val={val} set={set} commit={commit} pools={pools} codes={codes} adjust={adjust} />;
  if (kind === "lending") return <LendingForm pid={pos.id} pos={pos} cur={cur} val={val} set={set} commit={commit} pools={pools} codes={codes} adjust={adjust} />;
  return null;
}


function PositionCard({ pos, trades, txns, period, vis, form, onField, onCommit, accounts, codes, pools, adjust, onToggleWord, onEdit, onLogTrade, onDelTrade, onDel, checked, onToggleSelect, reload }) {
  const c = useMemo(() => computePosition(trades, pos.current_price, period), [trades, pos.current_price, period]);
  const cur = pos.currency || "";
  const txGroups = useMemo(() => {
    const sortT = (a, b) => String(b.date || "").localeCompare(String(a.date || "")) || String(b.created_at || "").localeCompare(String(a.created_at || ""));
    const sortX = (a, b) => String(b.at || b.created_at || "").localeCompare(String(a.at || a.created_at || ""));
    const sell = [...trades].filter((t) => t.side === "sell").sort(sortT).map((t) => ({ id: t.id, qty: `${fmtu(t.units)} @ ${fmt(n(t.price))}`, date: String(t.date || "").slice(0, 10) }));
    const buy = [...trades].filter((t) => t.side === "buy").sort(sortT).map((t) => ({ id: t.id, qty: `${fmtu(t.units)} @ ${fmt(n(t.price))}`, date: String(t.date || "").slice(0, 10) }));
    const byKind = (kind) => (txns || [])
      .filter((x) => x.kind === kind && !!pos.ticker && ((x.meta && x.meta.ticker) || "") === pos.ticker)
      .sort(sortX)
      .map((x) => ({ qty: `${cur} ${fmt(n(x.amount))}`, date: String(x.at || x.created_at || "").slice(0, 10) }));
    return { sell, buy, margin: byKind("lending_outlay"), project: byKind("lending_income") };
  }, [trades, txns, pos.ticker, cur]);
  const mkSet = (group) => (key, value, persist = true) => onField(pos.id, group, key, value, persist);
  const mkCommit = (group) => () => onCommit(pos.id, group);
  const chip = (key) => {
    const w = WORDS.find((x) => x.key === key); const active = vis.includes(key);
    return (
      <button key={key} type="button" onClick={() => onToggleWord(pos, key)} data-testid={`eq-word-${pos.id}-${key}`}
        className={`rounded px-1.5 py-0.5 text-[10px] font-semibold transition-colors ${active ? "bg-[var(--primary)] text-white" : "border border-black/15 opacity-55 hover:opacity-100 dark:border-white/15"}`}>{w.label}</button>
    );
  };
  return (
    <div className="rounded-lg border border-black/10 bg-white/60 p-3 dark:border-white/10 dark:bg-white/[0.03]" data-testid={`eq-pos-${pos.id}`}>
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-1.5">
          <input type="checkbox" checked={!!checked} onChange={() => onToggleSelect(pos.id)} data-testid={`eq-select-${pos.id}`}
            className="cursor-pointer accent-[var(--primary)]" title={TL("Select for bulk delete")} />
          {pos.exchange && <Badge>{pos.exchange}</Badge>}
          <span title={pos.name || "—"} data-testid={`eq-ticker-${pos.id}`} className="cursor-help font-head text-sm" style={{ textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: "3px" }}>{pos.ticker || "—"}</span>
          {cur && <Badge color="success">{cur}</Badge>}
        </div>
        <div className="flex flex-wrap gap-1" data-testid={`eq-words-${pos.id}`}>
          {ALL_KEYS.map(chip)}
        </div>
        <span className="ms-auto flex items-center gap-1">
          <button onClick={() => onLogTrade(pos, "buy")} data-testid={`eq-buy-${pos.id}`} className="inline-flex items-center gap-1 rounded bg-green-500/15 px-2 py-1 text-[11px] font-semibold text-green-700 hover:bg-green-500/25 dark:text-green-400"><TrendingUp size={12} /> {TL("Buy")}</button>
          <button onClick={() => onLogTrade(pos, "sell")} data-testid={`eq-sell-${pos.id}`} className="inline-flex items-center gap-1 rounded bg-red-500/15 px-2 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-500/25 dark:text-red-400"><TrendingDown size={12} /> {TL("Sell")}</button>
          <button onClick={() => onEdit(pos)} data-testid={`eq-edit-${pos.id}`} className="opacity-60 hover:opacity-100 hover:text-[var(--primary)]"><Pencil size={13} /></button>
          <AppraisalButton holding={pos} reload={reload} />
          <button onClick={() => onDel(pos)} data-testid={`eq-del-${pos.id}`} className="opacity-60 hover:opacity-100 hover:text-red-500"><Trash2 size={13} /></button>
        </span>
      </div>

      <div className="grid gap-3 xl:grid-cols-[1fr_auto]">
        <div className="grid grid-cols-3 gap-2" data-testid={`eq-groups-${pos.id}`}>
          {ALL_KEYS.map((key) => {
            const def = GROUP_DEF[key];
            if (!vis.includes(key)) {
              return (
                <div key={key} className="flex items-center justify-center rounded-md border border-dashed border-black/10 p-2 opacity-35 dark:border-white/10" data-testid={`eq-group-hidden-${key}-${pos.id}`}>
                  <span className="text-[10px] uppercase tracking-wide">{def.label} · hidden</span>
                </div>
              );
            }
            return (
              <div key={key} className={`rounded-md border p-2 ${TONE[def.tone]}`} data-testid={`eq-group-${key}-${pos.id}`}>
                <div className={`mb-1 text-[10px] font-semibold uppercase tracking-wide ${TONE_TX[def.tone]}`}>{def.label}</div>
                {def.form
                  ? <GroupForm kind={def.form} pos={pos} c={c} cur={cur} val={(form && form[def.form]) || {}} set={mkSet(def.form)} commit={mkCommit(def.form)} accounts={accounts} codes={codes} pools={pools} adjust={adjust} />
                  : <div className="grid grid-cols-3 gap-1.5">
                      {def.cells(c, cur).map(([label, val, tid]) => <Cell key={tid} label={label} value={val} testid={`eq-${tid}-${pos.id}`} />)}
                    </div>}
              </div>
            );
          })}
        </div>

        <div className="rounded-md border border-black/15 bg-black/[0.03] p-2 dark:border-white/15 dark:bg-white/[0.04]" data-testid={`eq-calc-${pos.id}`}>
          <div className="grid grid-cols-3 gap-1.5">
            <Cell label={TL("# sold")} value={fmtu(c.numSoldPeriod)} testid={`eq-numsold-${pos.id}`} />
            <Cell label={TL("# purchased")} value={fmtu(c.numPurchasedPeriod)} testid={`eq-numbought-${pos.id}`} />
            <Cell label={TL("# on hand")} value={fmtu(c.numOnHand)} testid={`eq-onhand-${pos.id}`} />
            <Cell label={TL("Value sold")} value={`${cur} ${fmt(c.valueSold)}`} testid={`eq-valsold-${pos.id}`} tone="text-red-600 dark:text-red-400" />
            <Cell label={TL("Value bought")} value={`${cur} ${fmt(c.valueBought)}`} testid={`eq-valbought-${pos.id}`} tone="text-green-700 dark:text-green-400" />
            <Cell label={TL("Net held")} value={`${cur} ${fmt(c.valueHeld)}`} testid={`eq-valheld-${pos.id}`} tone="text-[var(--primary)]" />
          </div>
          <div className="mt-1.5 grid grid-cols-2 gap-1.5" data-testid={`eq-txgroups-${pos.id}`}>
            <TxList label={TL("Sell transactions")} tone="text-red-700 dark:text-red-400" items={txGroups.sell} testid={`eq-tx-sell-${pos.id}`} onDel={(id) => onDelTrade({ id })} />
            <TxList label={TL("Buy transactions")} tone="text-green-700 dark:text-green-400" items={txGroups.buy} testid={`eq-tx-buy-${pos.id}`} onDel={(id) => onDelTrade({ id })} />
            <TxList label={TL("Margin transactions")} tone="text-amber-700 dark:text-amber-500" items={txGroups.margin} testid={`eq-tx-margin-${pos.id}`} />
            <TxList label={TL("Project transactions")} tone="text-amber-700 dark:text-amber-500" items={txGroups.project} testid={`eq-tx-project-${pos.id}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EquityLedger({ sector, positions = [], reload, createScope = null }) {
  const [trades, setTrades] = useState([]);
  const [period, setPeriod] = useState("year");
  const [scope, setScope] = useState("line");
  const [catVis, setCatVis] = useState(["sale", "purchase", "netted"]);
  const [posModal, setPosModal] = useState(null);
  const [tradeModal, setTradeModal] = useState(null);
  const [forms, setForms] = useState({});     // { [posId]: {income, tax, lending} }
  const [flags, setFlags] = useState({});     // { [posId]: {show_sold, show_bought, ...} }
  const [accounts, setAccounts] = useState([]);
  const [pools, setPools] = useState([]);     // Currencida pools [{id,name,balance,...}]
  const [txns, setTxns] = useState([]);       // adjust ledger (equity/lending money movements)
  const [schedOpen, setSchedOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(positions.map((p) => p.id));
      const n = new Set([...prev].filter((id) => ids.has(id)));
      return n.size === prev.size ? prev : n;
    });
  }, [positions]);

  const codes = useMemo(() => [...new Set([...BASE_CODES])], []);
  const loadTrades = () => client.get("/equity_trades").then((r) => setTrades((r.data || []).map((x) => ({ id: x.id, ...(x.data || x) })))).catch(() => {});
  const loadTxns = () => client.get("/transactions").then((r) => setTxns((r.data || []).map((x) => ({ id: x.id, ...(x.data || x) })))).catch(() => {});
  const loadPools = () => client.get("/currencies").then((r) => setPools((r.data || []).map((x) => ({ id: x.id, ...(x.data || x) })))).catch(() => {});
  const loadOptions = () => client.get("/options/holdings").then((r) => {
    const s = r.data?.equity_group_scope; if (Array.isArray(s) && s[0]) setScope(s[0]);
    const v = r.data?.equity_group_vis; if (Array.isArray(v)) setCatVis(v);
  }).catch(() => {});
  useEffect(() => {
    loadTrades(); loadOptions(); loadPools(); loadTxns();
    client.get("/options/currency").then((r) => {
      const a = r.data?.accounts || [];
      setAccounts(a.map((x) => (typeof x === "string" ? x : (x?.name || x?.number || ""))).filter(Boolean));
    }).catch(() => {});
    const h = () => loadOptions();
    window.addEventListener("fopfo:equity-vis-changed", h);
    return () => window.removeEventListener("fopfo:equity-vis-changed", h);
  }, []);

  const adjust = async (poolId, direction, amount, reason, meta) => {
    if (!poolId) return toast.error("Pick a Currencida account first.");
    if (!(Number(amount) > 0)) return toast.error("Nothing to move — amount is zero.");
    try {
      const r = await client.post("/currency/adjust", { pool_id: poolId, direction, amount: Number(amount), reason, meta: meta || {} });
      toast.success(`${direction === "debit" ? "Debited" : "Credited"} ${fmt(amount)} · new balance ${fmt(r.data.balance)}.`);
      loadPools(); loadTxns();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Transfer failed."); return false; }
  };
  useEffect(() => {
    setForms((prev) => {
      const next = { ...prev };
      positions.forEach((p) => { if (!next[p.id]) next[p.id] = { income: p.income || {}, tax: p.tax || {}, lending: p.lending || {} }; });
      return next;
    });
    setFlags((prev) => {
      const next = { ...prev };
      positions.forEach((p) => { if (!next[p.id]) next[p.id] = { show_sold: p.show_sold !== false, show_bought: p.show_bought !== false, show_held: p.show_held !== false, show_lending: !!p.show_lending, show_income: !!p.show_income, show_tax: !!p.show_tax }; });
      return next;
    });
  }, [positions]);

  const onField = (posId, group, key, value, persist) => {
    setForms((prev) => {
      const pf = prev[posId] || { income: {}, tax: {}, lending: {} };
      const g = { ...(pf[group] || {}), [key]: value };
      if (persist) client.put(`/holdings/${posId}`, { data: { [group]: g } }).catch(() => {});
      return { ...prev, [posId]: { ...pf, [group]: g } };
    });
  };
  const onCommit = (posId, group) => setForms((prev) => { client.put(`/holdings/${posId}`, { data: { [group]: prev[posId]?.[group] || {} } }).catch(() => {}); return prev; });

  const persistOption = (key, values) => client.put(`/options/holdings/${key}`, { values }).then(() => window.dispatchEvent(new CustomEvent("fopfo:equity-vis-changed"))).catch(() => {});
  const tradesFor = (pid) => trades.filter((t) => t.position_id === pid);
  const visFor = (pos) => scope === "category" ? catVis : ALL_KEYS.filter((k) => (flags[pos.id] || {})[flagOf(k)]);

  const toggleWord = (pos, key) => {
    if (scope === "category") {
      const next = catVis.includes(key) ? catVis.filter((k) => k !== key) : [...catVis, key];
      setCatVis(next); persistOption("equity_group_vis", next);
    } else {
      const flag = flagOf(key);
      setFlags((prev) => {
        const pf = prev[pos.id] || {};
        const nf = { ...pf, [flag]: !pf[flag] };
        client.put(`/holdings/${pos.id}`, { data: { [flag]: nf[flag] } }).catch(() => {});
        return { ...prev, [pos.id]: nf };
      });
    }
  };
  const setScopeMode = (m) => { setScope(m); persistOption("equity_group_scope", [m]); };
  const setAll = (on) => {
    if (scope === "category") {
      const next = on ? [...ALL_KEYS] : [];
      setCatVis(next); persistOption("equity_group_vis", next);
    } else {
      const patch = Object.fromEntries(WORDS.map((w) => [w.flag, on]));
      setFlags((prev) => {
        const next = { ...prev };
        positions.forEach((p) => { next[p.id] = { ...(prev[p.id] || {}), ...patch }; client.put(`/holdings/${p.id}`, { data: patch }).catch(() => {}); });
        return next;
      });
    }
  };

  const addSlots = async (count) => {
    try {
      for (let i = 0; i < count; i++) {
        await client.post("/holdings", { data: { ...blankPos(), category: "Equities", subcategory: sector, asset_class: "Equity", ...(createScope || {}) } });
      }
      toast.success(`Added ${count} blank slot${count > 1 ? "s" : ""} — fill them in inline.`);
      reload && reload();
    } catch (e) { toast.error("Could not add slots."); }
  };
  const isEmptySlot = (p) => !String(p.ticker || "").trim() && !String(p.name || "").trim() && tradesFor(p.id).length === 0;
  const emptySlots = positions.filter(isEmptySlot);
  const removeEmpty = async () => {
    if (!emptySlots.length) return;
    if (!window.confirm(`Remove ${emptySlots.length} empty slot${emptySlots.length > 1 ? "s" : ""} (no ticker, no name, no trades)? This cannot be undone.`)) return;
    try {
      await Promise.all(emptySlots.map((p) => client.delete(`/holdings/${p.id}`)));
      setSelected((prev) => { const n = new Set(prev); emptySlots.forEach((p) => n.delete(p.id)); return n; });
      toast.success(`Removed ${emptySlots.length} empty slot${emptySlots.length > 1 ? "s" : ""}.`); reload && reload();
    } catch (e) { toast.error("Delete failed."); }
  };
  const delSelected = async () => {
    const ids = [...selected].filter((id) => positions.some((p) => p.id === id));
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected position${ids.length > 1 ? "s" : ""}? This also removes their logged trades and cannot be undone.`)) return;
    try {
      await Promise.all(ids.map(async (id) => {
        await client.delete(`/holdings/${id}`);
        await Promise.all(tradesFor(id).map((t) => client.delete(`/equity_trades/${t.id}`).catch(() => {})));
      }));
      setSelected(new Set()); toast.success(`Deleted ${ids.length} position${ids.length > 1 ? "s" : ""}.`);
      loadTrades(); reload && reload();
    } catch (e) { toast.error("Delete failed."); }
  };
  const openEdit = (pos) => setPosModal({ mode: "edit", id: pos.id, data: { name: pos.name || "", exchange: pos.exchange || "", ticker: pos.ticker || "", currency: pos.currency || "", current_price: pos.current_price ?? "", settle_account: pos.settle_account || "" } });
  const savePos = async () => {
    const d = posModal.data;
    const base = posModal.mode === "add" ? blankPos() : {};
    const payload = { ...base, ...d, current_price: d.current_price === "" ? "" : n(d.current_price), category: "Equities", subcategory: sector, asset_class: "Equity", ...(createScope || {}) };
    try {
      if (posModal.mode === "edit") await client.put(`/holdings/${posModal.id}`, { data: payload });
      else await client.post("/holdings", { data: payload });
      toast.success(posModal.mode === "edit" ? "Position updated." : "Equity position added.");
      setPosModal(null); reload && reload();
    } catch (e) { toast.error("Could not save the position."); }
  };
  const delPos = async (pos) => {
    try {
      await client.delete(`/holdings/${pos.id}`);
      await Promise.all(tradesFor(pos.id).map((t) => client.delete(`/equity_trades/${t.id}`).catch(() => {})));
      toast.success("Position removed."); loadTrades(); reload && reload();
    } catch (e) { toast.error("Delete failed."); }
  };

  const openTrade = (position, side) => setTradeModal({ position, side, units: "", price: "", date: today(), settle_pool: position.settle_account || "", settle_now: true });
  const saveTrade = async () => {
    const t = tradeModal;
    if (!(n(t.units) > 0) || !(n(t.price) > 0)) return toast.error("Enter units and price greater than zero.");
    try {
      await client.post("/equity_trades", { data: { position_id: t.position.id, side: t.side, units: n(t.units), price: n(t.price), date: t.date } });
      if (t.settle_now && t.settle_pool) {
        await adjust(t.settle_pool, t.side === "buy" ? "debit" : "credit", n(t.units) * n(t.price), t.side === "buy" ? "equity_buy" : "equity_sell", { ticker: t.position.ticker, units: n(t.units), price: n(t.price) });
      }
      toast.success(`${t.side === "buy" ? "Buy" : "Sell"} logged.`); setTradeModal(null); loadTrades();
    } catch (e) { toast.error("Could not log the trade."); }
  };
  const delTrade = async (t) => { try { await client.delete(`/equity_trades/${t.id}`); loadTrades(); } catch (e) { /* noop */ } };
  const setPD = (k, v) => setPosModal((m) => ({ ...m, data: { ...m.data, [k]: v } }));

  const btn = "px-2 py-1 text-[10px]";
  return (
    <div className="space-y-2" data-testid={`eq-ledger-${sector}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex overflow-hidden rounded border border-black/15 text-[10px] dark:border-white/15" data-testid={`eq-period-${sector}`}>
          {PERIODS.map((p) => (
            <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`eq-period-${sector}-${p.key}`}
              className={`${btn} ${period === p.key ? "bg-[var(--primary)] text-white" : "opacity-70 hover:opacity-100"}`}>{p.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-wide opacity-50">{TL("Show groups:")}</span>
          <div className="flex overflow-hidden rounded border border-black/15 text-[10px] dark:border-white/15" data-testid={`eq-scope-${sector}`}>
            <button onClick={() => setScopeMode("line")} data-testid={`eq-scope-${sector}-line`} className={`${btn} ${scope === "line" ? "bg-[var(--primary)] text-white" : "opacity-70 hover:opacity-100"}`}>{TL("Per line")}</button>
            <button onClick={() => setScopeMode("category")} data-testid={`eq-scope-${sector}-category`} className={`${btn} ${scope === "category" ? "bg-[var(--primary)] text-white" : "opacity-70 hover:opacity-100"}`}>{TL("Whole category")}</button>
          </div>
          <button onClick={() => setAll(true)} data-testid={`eq-all-${sector}`} className={`${btn} rounded border border-black/15 opacity-70 hover:opacity-100 dark:border-white/15`}>{TL("All")}</button>
          <button onClick={() => setAll(false)} data-testid={`eq-none-${sector}`} className={`${btn} rounded border border-black/15 opacity-70 hover:opacity-100 dark:border-white/15`}>{TL("None")}</button>
        </div>
        <div className="ms-auto flex items-center gap-1" data-testid={`eq-add-group-${sector}`}>
          <span className="text-[10px] uppercase tracking-wide opacity-50">{TL("Add equity:")}</span>
          <div className="flex overflow-hidden rounded border border-black/15 text-[11px] dark:border-white/15">
            {[1, 5, 10].map((nn) => (
              <button key={nn} onClick={() => addSlots(nn)} data-testid={`eq-add-${sector}-${nn}`}
                className={`${btn} font-semibold hover:bg-[var(--primary)] hover:text-white`} title={`Add ${nn} blank slot${nn > 1 ? "s" : ""}`}>+{nn}</button>
            ))}
          </div>
        </div>
        <Button variant="ghost" onClick={() => setSchedOpen(true)} data-testid={`eq-scheduled-${sector}`} title={TL("Scheduled equity legs")}><CalendarClock size={14} /> {TL("Scheduled")}</Button>
      </div>

      {positions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-[11px]" data-testid={`eq-bulk-${sector}`}>
          <label className="inline-flex cursor-pointer items-center gap-1.5 opacity-70 hover:opacity-100">
            <input type="checkbox" data-testid={`eq-select-all-${sector}`}
              checked={selected.size === positions.length && positions.length > 0}
              ref={(el) => { if (el) el.indeterminate = selected.size > 0 && selected.size < positions.length; }}
              onChange={(e) => setSelected(e.target.checked ? new Set(positions.map((p) => p.id)) : new Set())} />
            Select all
          </label>
          {selected.size > 0 && (
            <button onClick={delSelected} data-testid={`eq-del-selected-${sector}`}
              className="inline-flex items-center gap-1 rounded border border-red-500/40 px-2 py-1 font-semibold text-red-600 hover:bg-red-500/10 dark:text-red-400">
              <Trash2 size={12} /> Delete selected ({selected.size})
            </button>
          )}
          {emptySlots.length > 0 && (
            <button onClick={removeEmpty} data-testid={`eq-remove-empty-${sector}`}
              className="inline-flex items-center gap-1 rounded border border-black/15 px-2 py-1 opacity-70 hover:opacity-100 dark:border-white/15">
              <Trash2 size={12} /> Remove empty slots ({emptySlots.length})
            </button>
          )}
        </div>
      )}

      {positions.length === 0 ? (
        <div className="rounded-lg border border-dashed border-black/15 py-6 text-center text-xs opacity-60 dark:border-white/15" data-testid={`eq-empty-${sector}`}>
          No equity positions in “{sector}” yet. Use “Add equity: +1 / +5 / +10” to create blank slots, fill in the ticker and details inline, then log Buy / Sell trades — averages, holdings and period values compute automatically.
        </div>
      ) : (
        <div className="space-y-2">
          {positions.map((pos) => (
            <PositionCard key={pos.id} pos={pos} trades={tradesFor(pos.id)} txns={txns} period={period} vis={visFor(pos)}
              form={forms[pos.id]} onField={onField} onCommit={onCommit} accounts={accounts} codes={codes} pools={pools} adjust={adjust}
              onToggleWord={toggleWord} onEdit={openEdit} onLogTrade={openTrade} onDelTrade={delTrade} onDel={delPos}
              checked={selected.has(pos.id)} onToggleSelect={toggleSelect} reload={reload} />
          ))}
        </div>
      )}

      {posModal && (
        <Modal open={!!posModal} onClose={() => setPosModal(null)} title={`${posModal.mode === "edit" ? "Edit" : "Add"} equity — ${sector}`} testid="eq-pos-modal">
          <div className="grid grid-cols-2 gap-3">
            <Input label={TL("Exchange")} maxLength={8} value={posModal.data.exchange} onChange={(e) => setPD("exchange", e.target.value)} data-testid="eq-f-exchange" />
            <Input label={TL("Ticker")} maxLength={8} value={posModal.data.ticker} onChange={(e) => setPD("ticker", e.target.value)} data-testid="eq-f-ticker" />
            <div className="col-span-2"><Input label={TL("Full name (shows on hover over the ticker)")} value={posModal.data.name} onChange={(e) => setPD("name", e.target.value)} data-testid="eq-f-name" /></div>
            <Select label={TL("Currency (Currencida)")} value={posModal.data.currency} onChange={(e) => setPD("currency", e.target.value)} data-testid="eq-f-currency">
              <option value="">{TL("— select —")}</option>
              {codes.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
            <Input label={TL("Current carrying price (mark)")} type="number" value={posModal.data.current_price} onChange={(e) => setPD("current_price", e.target.value)} data-testid="eq-f-current-price" />
            <label className="col-span-2 block text-sm"><span className="mb-1 block text-xs opacity-70">{TL("Settlement account (Currencida) — trades move cash here")}</span>
              <select className="w-full rounded border border-black/15 bg-white/70 px-2 py-1.5 text-sm dark:border-white/15 dark:bg-black/30" value={posModal.data.settle_account || ""} onChange={(e) => setPD("settle_account", e.target.value)} data-testid="eq-f-settle-account">
                <option value="">{TL("— none —")}</option>
                {pools.map((p) => <option key={p.id} value={p.id}>{p.name || p.id}{p.balance != null ? ` (${p.balance})` : ""}</option>)}
              </select>
            </label>
            <div className="col-span-2 flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setPosModal(null)}>{TL("Cancel")}</Button>
              <Button onClick={savePos} data-testid="eq-pos-save">{TL("Save")}</Button>
            </div>
          </div>
        </Modal>
      )}

      {tradeModal && (
        <Modal open={!!tradeModal} onClose={() => setTradeModal(null)} title={`Log ${tradeModal.side === "buy" ? "purchase" : "sale"} — ${tradeModal.position.ticker || tradeModal.position.name || "position"}`} testid="eq-trade-modal">
          <div className="grid grid-cols-2 gap-3">
            <Input label={TL("Units")} type="number" value={tradeModal.units} onChange={(e) => setTradeModal((m) => ({ ...m, units: e.target.value }))} data-testid="eq-t-units" />
            <Input label={`Price per unit${tradeModal.position.currency ? ` (${tradeModal.position.currency})` : ""}`} type="number" value={tradeModal.price} onChange={(e) => setTradeModal((m) => ({ ...m, price: e.target.value }))} data-testid="eq-t-price" />
            <div className="col-span-2"><Input label={TL("Date")} type="date" value={tradeModal.date} onChange={(e) => setTradeModal((m) => ({ ...m, date: e.target.value }))} data-testid="eq-t-date" /></div>
            <label className="col-span-2 block text-sm"><span className="mb-1 block text-xs opacity-70">{TL("Settle against account (Currencida)")}</span>
              <select className="w-full rounded border border-black/15 bg-white/70 px-2 py-1.5 text-sm dark:border-white/15 dark:bg-black/30" value={tradeModal.settle_pool || ""} onChange={(e) => setTradeModal((m) => ({ ...m, settle_pool: e.target.value }))} data-testid="eq-t-settle-pool">
                <option value="">{TL("— none (ledger only) —")}</option>
                {pools.map((p) => <option key={p.id} value={p.id}>{p.name || p.id}{p.balance != null ? ` (${p.balance})` : ""}</option>)}
              </select>
            </label>
            <label className="col-span-2 flex items-center gap-2 text-xs"><input type="checkbox" checked={!!tradeModal.settle_now} onChange={(e) => setTradeModal((m) => ({ ...m, settle_now: e.target.checked }))} data-testid="eq-t-settle-now" /> Move cash now — {tradeModal.side === "buy" ? "debit" : "credit"} {fmt(n(tradeModal.units) * n(tradeModal.price))}</label>
            <div className="col-span-2 flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setTradeModal(null)}>{TL("Cancel")}</Button>
              <Button onClick={saveTrade} data-testid="eq-t-save">Log {tradeModal.side === "buy" ? "buy" : "sell"}</Button>
            </div>
          </div>
        </Modal>
      )}

      <ScheduledActivity app="equity" open={schedOpen} onClose={() => { setSchedOpen(false); loadPools(); loadTxns(); }} />
    </div>
  );
}
